import { useMemo } from 'react';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { getImageFileData, humanFileSize, Icon, Icons, ImageType } from 'carrier-fe';

export const CAROUSEL_ACCEPTED_MIME_TYPES = {
	'image/jpeg': ['.jpeg', '.jpg'],
	'image/png': ['.png'],
	'image/gif': ['.gif'],
	'video/mp4': ['.mp4'],
};

const ACCEPTED_MIME_LIST = Object.keys(CAROUSEL_ACCEPTED_MIME_TYPES);

export function isVideoType(type?: string | null): boolean {
	return !!type && type.startsWith('video/');
}

type Props = {
	label?: string;
	value?: ImageType;
	disabled?: boolean;
	errorField?: boolean;
	onChange: (value: ImageType | null) => void;
	onReject?: (message: string) => void;
};

function CarouselMediaInput({
	label,
	value,
	disabled = false,
	errorField = false,
	onChange,
	onReject = () => {},
}: Props) {
	const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
		useDropzone({
			noDrag: disabled,
			noClick: disabled,
			maxFiles: 1,
			accept: CAROUSEL_ACCEPTED_MIME_TYPES,
			onDropAccepted: async (files) => {
				if (!files?.length) return;
				if (files.length > 1) {
					onReject(
						'Too many files attempted. Please select a single file.'
					);
					return;
				}

				const [file] = files;
				const data = await getImageFileData(file);
				onChange({
					...data,
					type: data.type || file.type,
					size_human: humanFileSize(file.size),
				});
			},
			onDropRejected: () => {
				onReject(
					'Unsupported file. Accepted types: JPG, JPEG, PNG, GIF, MP4.'
				);
			},
		});

	const classes = useMemo(() => {
		const list = [
			'carousel-media-dropzone',
			'border',
			'rounded',
			'p-3',
			'd-flex',
			'align-items-center',
			'justify-content-center',
		];
		if (errorField) list.push('is-invalid');
		if (isDragReject) list.push('border-danger', 'bg-white');
		else if (isDragAccept) list.push('border-success', 'bg-light');
		else if (isFocused) list.push('border-primary', 'bg-light');
		else list.push('bg-white');
		if (disabled) list.push('disabled');
		return classNames(list);
	}, [errorField, isFocused, isDragAccept, isDragReject, disabled]);

	if (value) {
		const previewSrc = value.base64 || value.url || '';
		const isVideo = isVideoType(value.type);

		return (
			<div className="carousel-media-input">
				<div
					className="border rounded p-3 d-flex align-items-center"
					style={{ gap: 16 }}
				>
					<div style={{ width: 200, flexShrink: 0 }}>
						{isVideo ? (
							<video
								src={previewSrc}
								controls
								muted
								preload="metadata"
								style={{
									width: '100%',
									maxHeight: 140,
									borderRadius: 4,
									background: '#000',
								}}
							/>
						) : (
							<img
								src={previewSrc}
								alt=""
								style={{
									width: '100%',
									maxHeight: 140,
									objectFit: 'contain',
									borderRadius: 4,
								}}
							/>
						)}
					</div>
					<div className="flex-fill">
						<div className="fw-bold">{value.name}</div>
						<div className="small text-muted">
							{value.type}
							{value.size_bytes
								? ` · ${value.size_human || humanFileSize(value.size_bytes)}`
								: ''}
						</div>
					</div>
					<button
						type="button"
						className="btn btn-sm btn-outline-danger"
						onClick={() => onChange(null)}
						disabled={disabled}
						aria-label="Remove media"
					>
						<Icon icon={Icons.CLOSE} />
					</button>
				</div>
				{!!label && (
					<label className="mt-1 d-block text-muted small">
						{label}
					</label>
				)}
			</div>
		);
	}

	return (
		<div className="carousel-media-input">
			<div {...getRootProps({ className: classes })}>
				<input
					{...getInputProps()}
					accept={ACCEPTED_MIME_LIST.join(',')}
				/>
				<div className="text-center">
					<Icon icon={Icons.IMAGE} className="mb-2" />
					<div className="mb-1">
						Drag 'n' drop or click to upload
					</div>
					<div className="small text-muted">
						JPG, JPEG, PNG, GIF, or MP4
					</div>
				</div>
			</div>
			{!!label && (
				<label className="mt-1 d-block text-muted small">
					{label}
				</label>
			)}
		</div>
	);
}

export default CarouselMediaInput;
